import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, LogIn, ArrowLeft } from "lucide-react";
import useBrand from "@/hooks/useBrand";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("admin@projectruda.pl");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    // Apply dynamic favicon based on uploaded admin logo
    const { logoSrc } = useBrand();

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-base)] text-[color:var(--text-secondary)]">
                <Loader2 className="animate-spin" />
            </div>
        );
    }
    if (user) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) navigate("/admin");
        else setError(res.error || "Logowanie nie powiodło się");
    };

    return (
        <div className="min-h-screen flex flex-col bg-[color:var(--bg-base)]">
            <div className="px-6 md:px-12 py-6">
                <Link
                    to="/"
                    data-testid="back-home-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)]"
                >
                    <ArrowLeft size={14} /> Strona główna
                </Link>
            </div>
            <div className="flex-1 flex items-center justify-center px-6">
                <form
                    onSubmit={onSubmit}
                    data-testid="admin-login-form"
                    className="panel-yellow w-full max-w-md p-10"
                >
                    {logoSrc && (
                        <div className="flex justify-center mb-6">
                            <img
                                src={logoSrc}
                                alt="Logo"
                                data-testid="admin-login-logo"
                                className="h-14 w-auto max-w-[180px] object-contain"
                            />
                        </div>
                    )}
                    <div className="label-eyebrow mb-3">Panel administratora</div>
                    <h1 className="font-display uppercase text-3xl text-white mb-8 tracking-tight">
                        Zaloguj się
                    </h1>

                    <label className="block text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)] mb-2">
                        Email
                    </label>
                    <input
                        data-testid="login-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-4 py-3 mb-5 text-white focus:border-[color:var(--primary)] outline-none"
                    />

                    <label className="block text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)] mb-2">
                        Hasło
                    </label>
                    <input
                        data-testid="login-password-input"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-4 py-3 mb-6 text-white focus:border-[color:var(--primary)] outline-none"
                    />

                    {error && (
                        <div
                            data-testid="login-error"
                            className="text-sm text-red-400 mb-4 border border-red-500/40 bg-red-500/5 px-3 py-2 rounded-md"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="login-submit-btn"
                        disabled={loading}
                        className="btn-primary w-full disabled:opacity-60"
                    >
                        {loading ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <LogIn size={16} />
                        )}
                        Zaloguj
                    </button>
                </form>
            </div>
        </div>
    );
}
